import type { PlaygroundRunSummary } from "contracts/api/playground";
import { CRAWL_RUN_STATUS, isActiveCrawlRunStatus } from "contracts/enums/core";
import { For, Show } from "solid-js";
import { relativeAge } from "../../ui/format";
import { createPlaygroundRuns } from "./playgroundQueries";

/** Every run for this URL, newest first. Click a row to load it into the
 * comparison; the first pick fills slot A, the second slot B, and a further
 * pick replaces slot A. */
export function RunHistoryStrip(props: {
  websiteId: string;
  slotA: string | null;
  slotB: string | null;
  onPick: (runId: string) => void;
}) {
  const runs = createPlaygroundRuns(props.websiteId);

  const slotOf = (runId: string): string | null =>
    props.slotA === runId ? "A" : props.slotB === runId ? "B" : null;

  return (
    <section class="panel">
      <div class="panel-head">
        <span class="panel-title">Runs</span>
        <span class="landing-sub">Pick two to compare</span>
      </div>
      <Show
        when={runs.data}
        fallback={
          <div class="loading" style={{ padding: "16px" }}>
            Loading runs…
          </div>
        }
      >
        {(data) => (
          <Show
            when={data().runs.length > 0}
            fallback={
              <div class="empty">
                <div class="empty-title">No runs yet</div>
                <div class="empty-hint">
                  Pick a prompt and scoring guide above, then run the agent.
                </div>
              </div>
            }
          >
            <div class="run-list">
              <For each={data().runs}>
                {(run) => (
                  <RunRow
                    run={run}
                    slot={slotOf(run.runId)}
                    onPick={() => props.onPick(run.runId)}
                  />
                )}
              </For>
            </div>
          </Show>
        )}
      </Show>
    </section>
  );
}

function RunRow(props: {
  run: PlaygroundRunSummary;
  slot: string | null;
  onPick: () => void;
}) {
  const counts = () => props.run.findingCounts;
  const total = () =>
    counts().critical + counts().high + counts().medium + counts().low;
  return (
    <button
      type="button"
      class={`run-item${props.slot ? " is-picked" : ""}`}
      onClick={props.onPick}
    >
      <span class={`slot-badge${props.slot ? " is-set" : ""}`}>
        {props.slot ?? "+"}
      </span>
      <span class="run-item-main">
        <span style={{ display: "flex", gap: "8px", "align-items": "center" }}>
          <StatusBadge status={props.run.status} />
          <span class="landing-sub">{relativeAge(props.run.startedAt)}</span>
        </span>
        <span class="run-item-params">
          <span class="badge">
            {props.run.promptBundleLabel ?? "active prompt"}
          </span>
          <span class="badge">
            {props.run.catalogDraftLabel ?? "canonical"}
          </span>
        </span>
      </span>
      <span class="run-item-scores">
        <span>agent {props.run.agentScore ?? "—"}</span>
        <span>human {props.run.humanScore ?? "—"}</span>
        <span>{total()} findings</span>
      </span>
    </button>
  );
}

function StatusBadge(props: { status: PlaygroundRunSummary["status"] }) {
  const cls =
    props.status === CRAWL_RUN_STATUS.FAILED
      ? "badge badge-sev sev-critical"
      : isActiveCrawlRunStatus(props.status)
        ? "badge badge-status st-in_progress"
        : "badge badge-status st-done";
  return <span class={cls}>{props.status}</span>;
}
